import { toast } from "sonner";
import { getFrankApiUrl, getFrankThreshold } from "@/utils/frankMessageUtils";
import { FrankApiResponse, Source } from "./types";
import { DESKTOP_TIMEOUT, MOBILE_TIMEOUT } from "./config";
import { isMobileDevice, logNetworkInformation, normalizeApiUrl } from "./utils";
import { handleSpecificErrors, logDetailedError, logFetchError } from "./errorHandler";
import { testFrankApiConnection } from "./apiTester";
import { frankLogger } from "./logger";

// Export types and functions for use in other modules
export type { Source, FrankApiResponse };
export { testFrankApiConnection };

export const askFrank = async (
  question: string, 
  associationId: string, 
  language: string = 'en',
  context?: { organizationId?: string; memberId?: string },
  retryCount: number = 0
): Promise<{answer: string, source?: string, sources?: Source[]}> => {
  const startTime = Date.now();
  const correlationId = frankLogger.generateCorrelationId();
  
  console.log(`[${correlationId}] Frank request initiated:`, { 
    question: question.substring(0, 100) + '...', 
    associationId, 
    language,
    context
  });

  // Convert associationId to a number - the API expects a number, not a UUID string
  const numericAssociationId = parseInt(associationId, 10);
  
  if (isNaN(numericAssociationId)) {
    const errorMsg = `Invalid associationId format. Expected numeric ID but got: ${associationId}`;
    console.error(`[${correlationId}] ${errorMsg}`);
    
    await frankLogger.logError({
      correlationId,
      organizationId: context?.organizationId,
      memberId: context?.memberId,
      question,
      language,
      errorType: 'INVALID_CONFIG',
      errorMessage: errorMsg,
      errorDetails: { receivedAssociationId: associationId, type: typeof associationId }
    });
    
    throw new Error("Invalid association ID format. Expected a numeric ID.");
  }
  
  const payload = {
    question,
    association_id: numericAssociationId, // Send as a number, not a string
    threshold: getFrankThreshold(),
    language: language // Add language to the payload
  };
  
  // Get the current API URL from localStorage
  let API_URL = getFrankApiUrl();
  
  console.log(`[${correlationId}] Frank AI Request Payload:`, JSON.stringify(payload, null, 2));
  console.log(`[${correlationId}] Frank API URL being used:`, API_URL);
  console.log(`[${correlationId}] Frank threshold being used:`, payload.threshold);
  
  // Check if we're on a mobile device for timeout settings
  const isMobile = isMobileDevice();
  const timeout = isMobile ? MOBILE_TIMEOUT : DESKTOP_TIMEOUT;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    // Get properly formatted API URL
    const apiUrl = normalizeApiUrl(API_URL);
    
    console.log(`[${correlationId}] Attempting to connect to Frank AI at:`, apiUrl);
    console.log(`[${correlationId}] Using timeout of:`, timeout, "ms");
    
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify(payload),
      mode: "cors" as RequestMode,
      credentials: "omit" as RequestCredentials,
      signal: controller.signal,
    };
    
    console.log(`[${correlationId}] Request options:`, JSON.stringify({
      method: requestOptions.method,
      headers: requestOptions.headers,
      mode: requestOptions.mode,
      credentials: requestOptions.credentials
    }, null, 2));
    
    try {
      // Log network information for debugging
      logNetworkInformation();
      
      const response = await fetch(apiUrl, requestOptions);
      
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;
      
      console.log(`[${correlationId}] Frank AI Response status:`, response.status, response.statusText, `(${responseTime}ms)`);
      
      if (!response.ok) {
        const errorResponseTime = Date.now() - startTime;
        const errorType = frankLogger.categorizeError(null, response);
        
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        let errorDetails: any = { status: response.status, statusText: response.statusText };
        
        // For 422 errors, try to get more information from the response
        if (response.status === 422) {
          try {
            const errorData = await response.json();
            console.error(`[${correlationId}] 422 Error details:`, errorData);
            errorMessage = `API Error: 422 Unprocessable Entity - ${errorData?.detail || 'Invalid input data'}`;
            errorDetails.apiErrorData = errorData;
          } catch (parseError) {
            errorMessage = `API Error: 422 Unprocessable Entity - Request contained invalid data`;
            errorDetails.parseError = parseError;
          }
        }
        
        // For 500 errors, provide more specific information and retry logic
        if (response.status === 500) {
          const maxRetries = 2;
          if (retryCount < maxRetries) {
            console.warn(`[${correlationId}] Server error (500), retrying in ${2 * (retryCount + 1)} seconds... (attempt ${retryCount + 1}/${maxRetries + 1})`);
            await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1))); // Exponential backoff
            return askFrank(question, associationId, language, context, retryCount + 1);
          }
          errorMessage = `Server error (500): The Frank AI service is experiencing issues after ${maxRetries + 1} attempts. Please try again in a few minutes.`;
        }
        
        // Only log error once (don't log retries for 500 errors)
        if (response.status !== 500 || retryCount >= 2) {
          await frankLogger.logError({
            correlationId,
            organizationId: context?.organizationId,
            memberId: context?.memberId,
            associationId: numericAssociationId,
            question,
            language,
            apiUrl,
            threshold: payload.threshold,
            responseTimeMs: errorResponseTime,
            errorType,
            errorCode: response.status,
            errorMessage,
            errorDetails: { ...errorDetails, retryCount }
          });
        }
        
        const apiError = new Error(errorMessage);
        (apiError as any).status = response.status;
        (apiError as any).logged = true; // Mark as already logged
        throw apiError;
      }
      
      const data: FrankApiResponse = await response.json();
      const successResponseTime = Date.now() - startTime;
      
      // Log the raw response for debugging
      console.log(`[${correlationId}] Frank AI Raw Response:`, JSON.stringify(data, null, 2));
      
      console.log(`[${correlationId}] Frank AI Response received, answer length:`, data?.answer?.length || 0);
      console.log(`[${correlationId}] Frank AI Response source:`, data?.source || "No source provided");
      console.log(`[${correlationId}] Frank AI Response sources:`, JSON.stringify(data?.sources || [], null, 2));
      
      if (!data || !data.answer) {
        const errorMsg = "Received empty response from Frank";
        await frankLogger.logError({
          correlationId,
          organizationId: context?.organizationId,
          memberId: context?.memberId,
          associationId: numericAssociationId,
          question,
          language,
          apiUrl,
          threshold: payload.threshold,
          responseTimeMs: successResponseTime,
          errorType: 'API_ERROR',
          errorMessage: errorMsg,
          errorDetails: { receivedData: data }
        });
        throw new Error(errorMsg);
      }
      
      // Ensure sources is properly formatted as an array
      let formattedSources = data.sources;
      
      // If sources is not an array but we have a source string, convert to proper format
      if ((!formattedSources || !Array.isArray(formattedSources) || formattedSources.length === 0) && data.source) {
        formattedSources = [{
          text: "Referenced content",
          source: data.source,
          page: undefined
        }];
      }

      // Log successful interaction
      await frankLogger.logSuccess({
        correlationId,
        organizationId: context?.organizationId,
        memberId: context?.memberId,
        associationId: numericAssociationId,
        question,
        language,
        apiUrl,
        threshold: payload.threshold,
        responseTimeMs: successResponseTime,
        answer: data.answer,
        sources: formattedSources
      });
      
      console.log(`[${correlationId}] Frank interaction completed successfully in ${successResponseTime}ms`);
      
      return {
        answer: data.answer,
        source: data.source,
        sources: formattedSources
      };
    } catch (fetchError: any) {
      const fetchErrorResponseTime = Date.now() - startTime;
      
      // Don't log again if this error was already logged as an API error
      if (!fetchError.logged) {
        logFetchError(fetchError);
        
        const errorType = frankLogger.categorizeError(fetchError as Error);
        console.error(`[${correlationId}] Fetch error (${errorType}):`, fetchError);
        
        await frankLogger.logError({
          correlationId,
          organizationId: context?.organizationId,
          memberId: context?.memberId,
          associationId: numericAssociationId,
          question,
          language,
          apiUrl: API_URL,
          threshold: payload.threshold,
          responseTimeMs: fetchErrorResponseTime,
          errorType,
          errorMessage: fetchError.message,
          errorDetails: { 
            name: fetchError.name,
            stack: fetchError.stack,
            isMobile,
            retryCount
          }
        });
      }
      
      // Handle specific error types
      const handledError = await handleSpecificErrors(fetchError as Error, isMobile, API_URL);
      throw handledError;
    }
  } catch (error: any) {
    // Don't log again if this error was already logged
    if (!error.logged) {
      console.error(`[${correlationId}] Unexpected error:`, error);
      logDetailedError(error);
    }
    throw error;
  }
};
